import { copyProgramContentSlidePagesBetweenOwners } from './programContentSlideService';
import { copyWorksheetBetweenOwners } from './programContentWorksheetService';
import { supabase } from '../supabaseClient';
import {
  findProgramContentAttachmentRefRestores,
  findProgramContentRefForks,
  isProgramContentRowUuid,
  type ProgramContentDiffPlan,
  type ProgramContentDiffRowLike,
  type ProgramContentRemoteAttachmentRefs,
} from '../utils/programContentRowsDiff';
import {
  readProgramLearningContentFields,
  writeProgramLearningContentFields,
} from '../utils/programLearningContentFields';

/** 차이 저장 DB 함수 미적용 환경(마이그레이션 전) — 기존 순차 저장으로 대체 */
export function isMissingProgramContentDiffRpcError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  if (e.code === 'PGRST202' || e.code === '42883') return true;
  return String(e.message ?? '').includes('Could not find the function');
}

/** 순서만 바뀐 행의 id가 서버에 없음(다른 기기에서 삭제 등) — 전체 값으로 재시도 */
export function isStaleBaselineProgramContentDiffRpcError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return String((error as { message?: string }).message ?? '').includes('stale baseline');
}

function newRefUuid(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const REMOTE_REF_ID_CHUNK = 100;

/**
 * 저장 직전: 바뀐 기존 행의 서버 현재 첨부 ref를 조회해, 이 화면에서 건드리지 않은 ref는 서버 값을 유지한다.
 * (다른 탭·기기가 방금 연결한 프레젠테이션·학습지를 오래된 화면이 null로 덮어쓰는 것 방지)
 */
export async function preserveRemoteProgramContentAttachmentRefs<T extends ProgramContentDiffRowLike>(
  table: 'program_scoped_content_rows' | 'program_fixed_content_rows',
  workspaceId: string,
  baseline: readonly ProgramContentDiffRowLike[],
  plan: ProgramContentDiffPlan<T>
): Promise<void> {
  const ids = Array.from(
    new Set(
      plan.changes
        .filter((c) => !c.positionOnly)
        .map((c) => String(c.row.id ?? '').trim().toLowerCase())
        .filter(isProgramContentRowUuid)
    )
  );
  if (!ids.length) return;
  const remoteById = new Map<string, ProgramContentRemoteAttachmentRefs>();
  try {
    for (let i = 0; i < ids.length; i += REMOTE_REF_ID_CHUNK) {
      const { data, error } = await supabase
        .from(table)
        .select('id, presentation_ref_id, worksheet_ref_id')
        .eq('workspace_id', workspaceId)
        .in('id', ids.slice(i, i + REMOTE_REF_ID_CHUNK));
      if (error) throw error;
      ((data ?? []) as Array<{ id: string } & ProgramContentRemoteAttachmentRefs>).forEach((r) => {
        remoteById.set(String(r.id).toLowerCase(), r);
      });
    }
  } catch (e) {
    console.warn('[program-content] 첨부 ref 서버 값 조회 실패 — 화면 값으로 저장', e);
    return;
  }
  const restores = findProgramContentAttachmentRefRestores(baseline, plan, remoteById);
  restores.forEach((restore) => {
    const change = plan.changes.find((c) => c.clientKey === restore.clientKey);
    if (!change) return;
    const fields = readProgramLearningContentFields(change.row);
    const written = writeProgramLearningContentFields({
      ...fields,
      ...(restore.presentationRefId ? { presentationRefId: restore.presentationRefId } : {}),
      ...(restore.worksheetRefId ? { worksheetRefId: restore.worksheetRefId } : {}),
    });
    change.row = { ...change.row, ...written };
    const snap = plan.snapshot.find((s) => s.clientKey === restore.clientKey);
    if (snap) snap.row = change.row;
  });
}

/** 증배·복사로 같은 첨부를 가리키게 된 새/변경 행: 첨부를 사본으로 복제한 뒤 행의 ref만 바꾼다(복제 실패 시 기존 공유 유지) */
export async function forkSharedProgramContentRefs<T extends ProgramContentDiffRowLike>(
  workspaceId: string,
  baseline: readonly ProgramContentDiffRowLike[],
  plan: ProgramContentDiffPlan<T>
): Promise<void> {
  const forks = findProgramContentRefForks(baseline, plan);
  for (const fork of forks) {
    const change = plan.changes.find((c) => c.clientKey === fork.clientKey);
    if (!change) continue;
    const toRef = newRefUuid();
    try {
      if (fork.kind === 'presentation') {
        await copyProgramContentSlidePagesBetweenOwners(
          workspaceId,
          { kind: 'presentation_ref', id: fork.fromRef },
          { kind: 'presentation_ref', id: toRef }
        );
      } else {
        await copyWorksheetBetweenOwners(workspaceId, fork.fromRef, toRef);
      }
    } catch {
      continue;
    }
    const fields = readProgramLearningContentFields(change.row);
    const written = writeProgramLearningContentFields(
      fork.kind === 'presentation' ? { ...fields, presentationRefId: toRef } : { ...fields, worksheetRefId: toRef }
    );
    change.row = { ...change.row, ...written };
    change.positionOnly = false;
  }
}
